import {useEffect, FC} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button, Avatar} from '@tickchak/react-share/app';
import {ClearIcon} from '@tickchak/react-share';
import Add from '../../../../../../assets/icons/add';
import useShow from '../../../../../../hooks/useShow';
import {updateReportContacts} from '../../../../../../redux/account/accountActions';
import {getAccountTeam} from '../../../../../../redux/team/teamActions';
import setGlobalState from '../../../../../../redux/setGlobalState';
import useTeamMembers from '../../../../../../hooks/useTeamMembers';
import EventRolePermission from '../../../../../components/rolePermission/EventRolePermission';
import AddContactPop from './AddContactPop';
import * as css from './ContactsSection.css';

interface ReportContact {
    globalUserId: number;
    sendInvoice?: number;
}

interface ContactMember {
    uid: number;
    globalUserId: number;
    nameFirst?: string;
    nameLast?: string;
    picture?: string;
    sendInvoice?: number;
}

const ContactsSection: FC = () => {
    const {t} = useTranslation('package');
    const pid = useSelector((s: any) => s.account.pid);
    const reportContacts: ReportContact[] = useSelector((s: any) => s.account.reportContacts) || [];
    const {accountMembers} = useTeamMembers() as {accountMembers: ContactMember[]};
    const addContactShow = useShow();

    useEffect(() => {
        if(pid && !accountMembers.length)
            getAccountTeam();
    }, [pid]);

    const contactMembers: ContactMember[] = reportContacts.map(contact => {
        const member = accountMembers.find(m => m.globalUserId === contact.globalUserId);
        return member ? {...member, sendInvoice: contact.sendInvoice} : null;
    }).filter(Boolean) as ContactMember[];

    const handleRemove = (globalUserId: number) => {
        const updated = reportContacts.filter(c => c.globalUserId !== globalUserId);
        setGlobalState('account.reportContacts', updated);
        updateReportContacts(updated).catch(() => setGlobalState('account.reportContacts', reportContacts));
    };

    const getInitials = (member: ContactMember): string =>
        (member.nameFirst?.[0] || '') + (member.nameLast?.[0] || '');

    return (
        <EventRolePermission rolePermission="manage package">
            <css.container>
                <css.title>{t('reportContacts')}</css.title>
                <css.contacts>
                    <css.popupWrap>
                        <Button variant="neutral" size="category" iconStart={<Add width={14} height={14} />} onClick={addContactShow.open}>
                            {t('addReportContact')}
                        </Button>
                        {addContactShow.isOpen && <AddContactPop show={addContactShow} contactMembers={contactMembers} />}
                    </css.popupWrap>
                    {contactMembers.map(member => (
                        <Button
                            key={member.uid}
                            variant="neutral"
                            size="category"
                            iconStart={<Avatar src={member.picture} initials={getInitials(member)} size="xs" />}
                            iconEnd={<ClearIcon size={16} color="#999" />}
                            onClick={() => handleRemove(member.globalUserId)}
                            customStyle={{paddingInlineStart: 4, paddingInlineEnd: 8}}
                        >
                            {member.nameFirst} {member.nameLast}
                        </Button>
                    ))}
                </css.contacts>
            </css.container>
        </EventRolePermission>
    );
};

export default ContactsSection;
